import * as Sentry from '@sentry/react'

// Sentry failing to report is invisible by default (the SDK only logs in debug mode), which is how a
// dead DSN went unnoticed. Warn once per page load so it at least shows up in the console.
let warned = false
function warnOnce(reason: string) {
  if (warned) return
  warned = true
  console.warn(`[sentry] error reporting is NOT working: ${reason}. Check VITE_SENTRY_DSN.`)
}

function loggingTransport(options: Parameters<typeof Sentry.makeFetchTransport>[0]) {
  const inner = Sentry.makeFetchTransport(options)
  return {
    ...inner,
    async send(envelope: Parameters<typeof inner.send>[0]) {
      try {
        const res = await inner.send(envelope)
        // 429 = rate limited, not a config problem.
        if (res.statusCode && res.statusCode >= 400 && res.statusCode !== 429) {
          warnOnce(`ingest returned HTTP ${res.statusCode}`)
        }
        return res
      } catch (e) {
        warnOnce(`ingest request failed (${e instanceof Error ? e.message : String(e)})`)
        throw e
      }
    },
  }
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    if (import.meta.env.PROD) warnOnce('VITE_SENTRY_DSN is not set in this build')
    return
  }
  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      transport: loggingTransport,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.captureConsoleIntegration({ levels: ['error'] }),
      ],
      beforeSend(event) {
        const msg = event.exception?.values?.[0]?.value || ''
        if (/AbortError|Non-Error promise rejection|ResizeObserver loop/i.test(msg)) return null
        return event
      },
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
        'Network request failed',
      ],
    })
  } catch (e) {
    // Monitoring must never be able to stop the app from booting.
    console.warn('[sentry] init failed, continuing without error reporting:', e)
  }
}
